import { useTranslation } from 'react-i18next';

import { looseListT } from '@/i18n/looseT';

import { bootStatusKeys, type BootPhase } from './bootStatus';
import { useBootElapsedMs } from './useBootElapsedMs';

/**
 * BootSplash — 冷啟動全螢幕狀態畫面(`RequireAuth` 兩道 gate 共用)
 * -----------------------------------------------------------------------------
 * 取代原本兩處各寫一行的 `t('status.loading')`(「載入中⋯⋯」)。那句在 Cloud Run
 * 冷啟動下會**靜止十幾秒不動**,使用者讀到的是「當掉了」而不是「在等」——owner
 * 2026-09-11:「讓他跳過冷啟動的時間,讓用戶不會感到困擾」。
 *
 * 兩件事讓等待變得可讀:
 *   1. **階段句跟著真實 gate 走**(`phase`)—— auth bootstrap → workspace bootstrap,
 *      畫面上的句子換了代表系統真的往前了一步,不是動畫在跑。
 *   2. **等久了才補說明**(4s / 12s 兩階,`bootStatus.ts` 純函式)。
 *
 * ⚠️ **計時器故意放元件、判準故意放純函式** —— 用 fake timer 驗文案切換會跟 i18n 的
 * async setup 打架(`ReportsDatePicker.test` 檔頭記過這個坑),所以時間邏輯在
 * `bootStatus.test.ts` 測,這裡只測「接線對不對」。
 *
 * ⚠️ **`phase` 換值時本元件不會 unmount**(React 在同一位置看到同型別元件會保留 state)
 * → `elapsedMs` 是**整段冷啟動**的累計,不是每個階段各自從 0 開始。這是刻意的:
 * 使用者感受到的是一段連續的等待,分段重計會讓 12 秒的說明永遠不出現。
 *
 * ⚠️ **動畫交給全域 `prefers-reduced-motion`**(`global.css` 把所有 keyframes 壓成
 * 0.01ms)—— 這裡直接用既有的 `spin`,不要自己再寫一份 media query。
 */
export function BootSplash({ phase }: { phase: BootPhase }) {
  const { t } = useTranslation('common');
  const { titleKey, hintIndex } = bootStatusKeys(phase, useBootElapsedMs());
  const hint = hintIndex === null ? null : looseListT(t)<string>('boot.hints')[hintIndex];

  return (
    <main
      role="status"
      aria-live="polite"
      data-testid="boot-splash"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        background: 'var(--ink-950)',
        color: 'var(--ink-300)',
        textAlign: 'center',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '2px solid var(--hairline)',
          borderTopColor: 'var(--tide-600)',
          animation: 'spin 0.9s linear infinite',
        }}
      />
      <span data-testid="boot-splash-title" style={{ fontSize: 14, fontWeight: 500 }}>
        {t(titleKey)}
      </span>
      {hint != null && (
        <span
          data-testid="boot-splash-hint"
          // ⚠️ 固定高度 —— 10 句長短不一,不固定的話每 3 秒整段文字會上下抽動
          style={{
            fontSize: 12,
            color: 'var(--ink-500)',
            maxWidth: 320,
            lineHeight: 1.6,
            minHeight: 38,
          }}
        >
          {hint}
        </span>
      )}
    </main>
  );
}

/**
 * `BootSyncLine` — 首頁資料載入中的**行內**狀態列(全螢幕版的小尺寸兄弟)。
 *
 * 為什麼首頁不用全螢幕版:進到首頁時骨架卡已經畫出來了,蓋一層全螢幕反而是倒退。
 * 這裡要補的是「骨架代表『還在載』還是『就是空的』」這個歧義 —— 一句話講明白。
 *
 * ⚠️ **放在問候列裡當第三個 baseline 項目**(見 `HomeGreeting`)—— 收起來的時候
 * 那一列高度不變,不會把下面整片內容往上抽一格(載入中的版面跳動比沒有提示更煩)。
 */
export function BootSyncLine() {
  const { t } = useTranslation('common');
  const { titleKey, hintIndex } = bootStatusKeys('sync', useBootElapsedMs());
  // 行內版空間小,輪播句取代階段句(不另起一行,免得把問候列撐開)
  const hint = hintIndex === null ? null : looseListT(t)<string>('boot.hints')[hintIndex];
  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="boot-sync-line"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 13,
        color: 'var(--ink-500)',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          border: '2px solid var(--hairline)',
          borderTopColor: 'var(--tide-600)',
          animation: 'spin 0.9s linear infinite',
        }}
      />
      {hint ?? t(titleKey)}
    </span>
  );
}
